/**
 * CommandPalette (Phase 21.2)
 * ----------------------------
 * Modal de búsqueda global activado con cmd/ctrl+K.
 *
 * Tres bloques:
 *  - Pacientes (autocomplete contra /api/search/quick).
 *  - Acciones rápidas (Nuevo paciente, Nuevo cobro, Subir agenda, etc.).
 *  - Atajos a secciones (todas las del sidebar).
 *
 * Roles:
 *  - Visible para todos.
 *  - Algunas acciones/atajos solo aparecen para gestión o admin, con la
 *    misma marca que en el menú (CommandPalette.test.jsx lo compara).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { telefonoConPrefijo } from '../lib/utils';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator,
} from './ui/command';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isAdmin, isGestion } from '../lib/roles';
import MicButton from './MicButton';
import {
  User, Receipt, FileText, ShieldCheck,
  CalendarDays, Users, ClipboardList, Scissors, Pill, BedDouble,
  Megaphone, Settings, FilePen, Stethoscope, ListChecks, Syringe, Clock, CalendarCog,
  UserPlus, MessagesSquare,
} from 'lucide-react';
import AgenteIcono from './AgenteIcono';

function useDebounced(value, delay = 200) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return d;
}

/** Acciones rápidas según rol. Las funciones reciben `navigate`.
 *  Presupuestos y cobros van con `gestion`, la misma raya que el menú
 *  (supervisión del 23/9/2026): «Nuevo presupuesto» salía a enfermería y a
 *  los anestesistas —el importe es confidencial (18/8/2026)— y
 *  «Registrar cobro» solo a dirección, cuando el menú y el servidor dejan a
 *  las administrativas llevar la caja del día (17/9/2026). */
function quickActionsFor(gestion, admin = false) {
  const actions = [
    {
      key: 'qa-new-patient',
      label: 'Crear nuevo paciente',
      hint: 'Abre la lista de pacientes',
      icon: User,
      run: (navigate) => navigate('/pacientes?new=1'),
    },
    {
      key: 'qa-dar-cita',
      label: 'Dar cita',
      hint: 'Abrir la agenda de citas',
      icon: CalendarDays,
      run: (navigate) => navigate('/citas'),
    },
    {
      key: 'qa-consent',
      label: 'Generar consentimiento',
      hint: 'Consentimientos informados',
      icon: FilePen,
      run: (navigate) => navigate('/consentimientos'),
    },
    {
      key: 'qa-new-budget',
      label: 'Nuevo presupuesto',
      hint: 'Abrir presupuestos',
      icon: FileText,
      run: (navigate) => navigate('/presupuestos'),
      gestionOnly: true,
    },
    {
      key: 'qa-anestesia',
      label: 'Anestesia del día',
      hint: 'Anestesistas por quirófano',
      icon: Syringe,
      run: (navigate) => navigate('/anestesia'),
    },
    {
      key: 'qa-lopd-tablet',
      label: 'Modo tablet (LOPD)',
      hint: 'Firma en consulta',
      icon: ShieldCheck,
      run: () => window.open('/firma-lopd', '_blank'),
    },
  ].filter((a) => !a.gestionOnly || gestion);
  if (gestion) {
    actions.splice(1, 0, {
      key: 'qa-new-cobro',
      label: 'Registrar cobro',
      hint: 'Abrir módulo de cobros',
      icon: Receipt,
      run: (navigate) => navigate('/cobros'),
    });
  }
  if (admin) {
    // Importes de externos: solo administradores (2026-09-08, punto 6).
    actions.push({
      key: 'qa-registro-externos',
      label: 'Registro de externos',
      hint: 'Facturación del quirófano a cirujanos externos',
      icon: Clock,
      run: (navigate) => navigate('/registro-externos'),
    });
  }
  return actions;
}

const NAV_SHORTCUTS_ALL = [
  { key: 'nav-dashboard', to: '/', label: 'Dashboard', icon: ListChecks },
  // Chat interno (23/9/2026): el mismo público que su entrada del menú y su
  // ruta (gestión). Qué salas ve cada uno lo decide el servidor.
  { key: 'nav-chat', to: '/chat', label: 'Chat', icon: MessagesSquare, gestionOnly: true },
  { key: 'nav-agenda', to: '/citas', label: 'Agenda', icon: CalendarDays },
  { key: 'nav-quirofano', to: '/quirofano-sesiones', label: 'Quirófano', icon: Scissors },
  { key: 'nav-anestesia', to: '/anestesia', label: 'Anestesia', icon: Syringe },
  { key: 'nav-jornada', to: '/registro-jornada', label: 'Registro de jornada', icon: Clock },
  { key: 'nav-pacientes', to: '/pacientes', label: 'Pacientes', icon: Users },
  // Leads (M16, 23/9/2026): estaba en el menú y no en el ⌘K. Mismo público
  // que el menú y que el servidor: gestión (admin, dirección, administrativas
  // y sus roles viejos).
  { key: 'nav-leads', to: '/leads', label: 'Leads / Contactos', icon: UserPlus, gestionOnly: true },
  // Presupuestos y Cobros, con la misma marca que en el menú (23/9/2026):
  // Presupuestos salía a cualquier rol y Cobros solo a dirección.
  { key: 'nav-presupuestos', to: '/presupuestos', label: 'Presupuestos', icon: FileText, gestionOnly: true },
  { key: 'nav-lopd', to: '/lopd', label: 'LOPD', icon: ShieldCheck },
  { key: 'nav-preoperatorios', to: '/preoperatorios', label: 'Pruebas preop.', icon: Stethoscope },
  { key: 'nav-indicaciones', to: '/indicaciones', label: 'Indicaciones cirugía', icon: ListChecks },
  { key: 'nav-recetas', to: '/recetas', label: 'Recetas', icon: Pill },
  { key: 'nav-postop', to: '/postoperatorios', label: 'Postoperatorios', icon: BedDouble },
  { key: 'nav-justificantes', to: '/justificantes', label: 'Justificantes', icon: FilePen },
  { key: 'nav-consents', to: '/consentimientos', label: 'Consentimientos', icon: FilePen },
  { key: 'nav-cobros', to: '/cobros', label: 'Cobros', icon: Receipt, gestionOnly: true },
  { key: 'nav-externos', to: '/registro-externos', label: 'Registro externos', icon: Clock, adminOnly: true },
  { key: 'nav-registro', to: '/registro-quirurgico', label: 'Registro QX', icon: ClipboardList },
  { key: 'nav-agendas', to: '/gestion-agendas', label: 'Gestión de agendas', icon: CalendarCog, adminOnly: true },
  { key: 'nav-catalogo', to: '/catalogo-cirugias', label: 'Catálogo cirugías', icon: Scissors },
  { key: 'nav-marketing', to: '/marketing', label: 'Marketing', icon: Megaphone, adminOnly: true },
  { key: 'nav-usuarios', to: '/usuarios', label: 'Usuarios', icon: Users, adminOnly: true },
  { key: 'nav-settings', to: '/configuracion', label: 'Configuración', icon: Settings },
];

export function useCommandPaletteShortcut(onToggle) {
  useEffect(() => {
    const handler = (e) => {
      const isMacCmd = e.metaKey && e.key.toLowerCase() === 'k';
      const isCtrlK = e.ctrlKey && e.key.toLowerCase() === 'k';
      if (isMacCmd || isCtrlK) {
        e.preventDefault();
        onToggle();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onToggle]);
}

export default function CommandPalette({ open, onOpenChange, onAskAgente }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = isAdmin(user);
  // Gestión: la misma raya que el menú (`gestionOnly` en AppShell) y que
  // `require_gestion()` del servidor. Hasta el 23/9 la paleta solo sabía de
  // dirección y de admin; ya no queda nada «solo de dirección» en el ⌘K.
  const gestion = isGestion(user);

  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 220);
  const [patients, setPatients] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!debounced || debounced.trim().length < 2) {
      setPatients([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    api.get(`/api/search/quick?q=${encodeURIComponent(debounced.trim())}&limit=10`)
      .then((r) => { if (!cancelled) setPatients(r.data.patients || []); })
      .catch(() => { if (!cancelled) setPatients([]); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [debounced, open]);

  // Reset state when dialog closes.
  useEffect(() => {
    if (!open) { setQuery(''); setPatients([]); }
  }, [open]);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  const goTo = useCallback((to) => { close(); navigate(to); }, [close, navigate]);

  const actions = useMemo(() => quickActionsFor(gestion, admin), [gestion, admin]);
  const shortcuts = useMemo(
    () => NAV_SHORTCUTS_ALL.filter((s) => {
      if (s.adminOnly && !admin) return false;
      if (s.gestionOnly && !gestion) return false;
      return true;
    }),
    [admin, gestion],
  );

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false}>
      <div className="relative">
        <CommandInput
          placeholder="¿Qué quieres hacer? Escribe o dicta con el micro…"
          value={query}
          onValueChange={setQuery}
          className="pr-10"
          data-testid="command-palette-input"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <MicButton
            onText={(t) => setQuery((prev) => (prev ? `${prev.trim()} ${t}` : t))}
            className="h-8 w-8"
            title="Dictar la búsqueda"
          />
        </div>
      </div>
      <CommandList data-testid="command-palette-list">
        {/* La pista promete la IA solo a quien la tiene: sin `onAskAgente`
            (el anestesista, D7 22/9/2026) no sale «Preguntar al Agente» y la
            pantalla no puede ofrecer lo que no va a hacer. */}
        {!query && (
          <div className="px-3 py-2 text-[11px] text-muted-foreground">
            {onAskAgente
              ? 'Escribe el nombre de un paciente, pídelo en lenguaje natural a la IA («dale cita a María el martes»), o elige una acción o sección.'
              : 'Escribe el nombre de un paciente, o elige una acción o sección.'}
          </div>
        )}

        {/* IA en lenguaje natural. Una sola entrada (2026-07-26): antes había
            aquí dos, «Agente IA» y «IA de citas», que eran motores distintos
            para lo mismo. Ahora todo va al Agente, que ya lleva la agenda. */}
        {onAskAgente && query.trim().length >= 2 && (
          <>
            <CommandGroup heading="Asistente IA">
              <CommandItem
                value={`agente ${query}`}
                onSelect={() => { close(); onAskAgente(query.trim()); }}
                data-testid="command-palette-agente"
              >
                <AgenteIcono size={18} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium">Preguntar al Agente IA</div>
                  <div className="text-[11px] text-muted-foreground truncate">«{query.trim()}» — citas, agenda, historias, recuentos…</div>
                </div>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        {/* Empty state inteligente: muestra nothing si está buscando */}
        {(!searching && debounced.length >= 2 && patients.length === 0) && (
          <CommandEmpty>Sin resultados para «{debounced}»</CommandEmpty>
        )}

        {patients.length > 0 && (
          <CommandGroup heading="Pacientes">
            {patients.map((p) => (
              <CommandItem
                key={p.id}
                value={`patient ${p.name} ${p.nif || ''} ${p.phone || ''}`}
                onSelect={() => goTo(`/pacientes/${p.id}`)}
                data-testid={`command-palette-patient-${p.id}`}
              >
                <User className="w-4 h-4 text-[#2E6E5E]" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{p.name || '(sin nombre)'}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {[p.nif, telefonoConPrefijo(p.phone), p.city].filter(Boolean).join(' · ') || p.email || '—'}
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {patients.length > 0 && <CommandSeparator />}

        <CommandGroup heading="Acciones rápidas">
          {actions.map((a) => {
            const Icon = a.icon;
            return (
              <CommandItem
                key={a.key}
                value={`action ${a.label}`}
                onSelect={() => { close(); a.run(navigate); }}
                data-testid={`command-palette-action-${a.key}`}
              >
                <Icon className="w-4 h-4 text-[#13231E]" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{a.label}</div>
                  {a.hint && <div className="text-[11px] text-muted-foreground truncate">{a.hint}</div>}
                </div>
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Ir a…">
          {shortcuts.map((s) => {
            const Icon = s.icon;
            return (
              <CommandItem
                key={s.key}
                value={`goto ${s.label}`}
                onSelect={() => goTo(s.to)}
                data-testid={`command-palette-nav-${s.key}`}
              >
                <Icon className="w-4 h-4 text-[#13231E]" />
                <span>{s.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
